<template>
    <aside class="order-overview" :class="{ 'fixed': aboveViewport }" id="basket-overview">
        <h2>Your Qoute</h2>
        <button class="icon-basket">Basket</button>
        <p v-if="basketStore.full">You have {{ basketStore.itemQuantityCount }} items in your quote</p>
        <p v-else>You don't currently have an quote</p>

        <form class="basket-items__content">
            <div class="basket-items" v-for="(items, category) in basketStore.squashItemsIntoProductVariantLine" :key="category.id">
                <h3 class="basket-items__heading">{{ category }}</h3>

                <div class="basket-items__days">
                    <span class="basket-items__day" v-for="day in basketStore.deliveryDays" :key="day">{{ day.slice(0, 3) }}</span>
                </div>

                <div class="basket-items__list">
                    <template v-for="line in items" :key="line">
                        <div class="basket-items__item">
                            <img class="basket-items__thumbnail" :src="getItemByName(line).image">
                            <p class="basket-items__name">
                                {{ line }}<br>
                                <span class="basket-items__price">£{{ getItemByName(line).price.toFixed(2) }} ea</span>

                                    <ToolTip :id="line" v-if="getItemByName(line).bulkPricingToolTip">
                                        <BulkPricingExplained :content="getItemByName(line).bulkPricingToolTip"/>
                                    </ToolTip>

                                <br>
                                <button class="basket-items__remove" type="button" @click="removeAllItemsById(getItemByName(line).id)">Remove</button>
                            </p>
                            <div class="basket-items__controls" v-for="day in basketStore.deliveryDays" :key="day">
                                <input class="basket-items__increment" type="button" value="▲" @click="incrementItemByName(line, day)">
                                <input class="basket-items__qty" type="number" min="0" max="1000" :value="getQuantityForNameAndDay(line, day)" readonly>
                                <input class="basket-items__decrement" type="button" value="▼" @click="decrementItemByName(line, day)">
                            </div>
                        </div>
                        <div class="basket-items__details">
                            <p class="basket-items__line-total">
                                <span class="basket-items__line-total-label">Item total:</span>
                                £{{ getItemSubtotal(line).toFixed(2) }}
                            </p>
                        </div>
                    </template>
                </div>
                <div class="basket-items__category-total">
                    <p class="basket-items__category-total-item">
                        <span class="basket-items__line-total-label">{{ category }} total: </span>
                        <strong>£{{ basketStore.categoryTotal(category).toFixed(2) }}</strong>
                    </p>
                </div>
            </div>
        </form>

        <ul class="basket-items__totals" v-if="basketStore.full">
            <li class="totals">
                <dl>
                    <dt>Total Products (excl. VAT)</dt><dd>£{{ (basketStore.totalExcTax).toFixed(2) }}</dd>
                    <dt>Total VAT</dt><dd>£{{ (basketStore.totalTax).toFixed(2) }}</dd>
                    <dt>Quote Total</dt><dd>£{{ (basketStore.totalIncTax).toFixed(2) }}</dd>
                    <dt class="product--total">Grand total</dt><dd class="product--total">£{{ (basketStore.totalIncTax).toFixed(2) }}</dd>
                </dl>
                <button v-if="basketStore.totalIncTax > 0" id="submit" type="button" class="button button--1 js-launch-popup" data-popup="checkout" @click="updateQuoteForm">Send quote</button>
            </li>
        </ul>

        <div v-if="admin">
            <hr>
            <textarea name="basketContents" v-model="basketStore.basketSummary"></textarea>
            <button @click="addTestItem">Add Item</button>
            <button @click="clearItems">Clear Items</button>
        </div>
    </aside>
</template>

<script setup>
    import { onMounted, onUnmounted, ref, computed } from 'vue';
    import { useBasketStore } from './store';
    import ToolTip from './ToolTip.vue';
    import BulkPricingExplained from './BulkPricingExplained.vue';

    const props = defineProps({
        admin: {
            type: Boolean,
            required: false,
            default: false
        }
    });

    const basketStore = useBasketStore();

    let basketStartPosition = ref(0);

    let aboveViewport = ref(false);

    function setStartPosition() {
        const basket = document.getElementById('basket-overview');
        const rect = basket.getBoundingClientRect();
        basketStartPosition.value = rect.top;
    }

    const isBasketAboveViewport = function() {
        aboveViewport.value = scrollY > basketStartPosition.value;
    };

    function addTestItem() {
        basketStore.addItem({ name: 'Test Item', price: 10.00, quantity: 1, image: 'https://officedrop.co.uk/images/uploads/seasonal-fruitbox.jpg', id: 55, deliveryDay: 'Monday', vat: true });
    }

    function clearItems() {
        basketStore.clearItems();
    }

    function removeItem(id, deliveryDay) {
        basketStore.removeItem(id, deliveryDay);
    }

    function removeAllItemsById(id) {
        basketStore.removeAllItemsById(id);
    }

    function updateQuoteForm() {
        const element = document.getElementById('quote-basket-summary');
        if (element) {
            element.innerHTML = basketStore.basketSummary;
        }

        const elementJson = document.getElementById('quote-basket-data');
        if (elementJson) {
            elementJson.value = basketStore.basketJson;
        }
    }

    function getItemByName(name) {
        return basketStore.items.find(item => item.name === name);
    }

    function getItemSubtotal(name) {
        const items = basketStore.items.filter(item => item.name === name);
        return items.reduce((total, item) => total + (basketStore.lineItemSubtotal(item)), 0);
    }

    function handleQuoteFormSubmit(event) {
        if (event.target && event.target.getAttribute('data-handle') === 'createAQuote') {
            event.target.addEventListener('freeform-ajax-success', function(event) {
                clearItems();
            });
        }
    }

    onMounted(() => {
        document.addEventListener('submit', handleQuoteFormSubmit, true);
        setStartPosition();
        document.addEventListener('scroll', () => {
            isBasketAboveViewport();
        });
    });

    onUnmounted(() => {
        document.removeEventListener('submit', handleQuoteFormSubmit, true);
    });

    function getQuantityForNameAndDay(productName, deliveryDay) {
        const item = basketStore.items.find(item => item.name === productName && item.deliveryDay === deliveryDay);
        return item ? item.quantity : 0;
    }

    function incrementItemByName(productName, deliveryDay) {
        const existingItem = basketStore.items.find(item => item.name === productName && item.deliveryDay === deliveryDay);
        
        if (!existingItem) {
            // Find any item with this name to get the base properties
            const templateItem = basketStore.items.find(item => item.name === productName);
            if (templateItem) {
                basketStore.addItem({
                    ...templateItem,
                    quantity: 1,
                    deliveryDay
                });
            }
            return;
        }
        existingItem.quantity++;
    }

    function decrementItemByName(productName, deliveryDay) {
        const item = basketStore.items.find(item => item.name === productName && item.deliveryDay === deliveryDay);
        if (item && item.quantity > 0) {
            item.quantity--;
        }
        if (item.quantity === 0) {
            item.quantity = 0;
        }
    }
</script>

<style scoped>
    @media (min-width: 1024px) {
        .button {
            position: fixed;
            bottom: 10px;
            right: 24px;
            width: calc(33.3% - 48px);
            min-width: 450px;
        }
    }
</style>